import { useQuery } from '@tanstack/react-query'
import { ReactNode } from 'react'
import { getMeFactorConfiguration } from 'api/factors'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import { getEnabledCurrencyDropDownOptions, isCurrencyDropDownOptions } from 'constants/options'
import { qkGetFactorConfiguration } from 'constants/QueryKeys/factor'
import TypoGraph from 'components/ui/dataDisplay/TypoGraph'
import TextField, { TextFieldProps } from './TextField'
import { DropDownOptions } from 'types/common'

export type DropdownFieldProps = TextFieldProps & {
  /** The option is used to pass an array of objects that represent the options displayed in the dropdown menu. 
  Each object within the array must have two properties: label and value. */
  options?: DropDownOptions[]
  /** The notOptionText is a optional props to display a custom message when not have options. */
  notOptionText?: string
  menuNode?: ReactNode
  endAdornment?: ReactNode
}

const DropdownField = (props: DropdownFieldProps) => {
  const { options = [], notOptionText, menuNode } = props
  const isCurrencyDropdown = isCurrencyDropDownOptions(options)
  const { data: factorConfigurationResponse } = useQuery({
    queryKey: qkGetFactorConfiguration(),
    queryFn: getMeFactorConfiguration,
    enabled: isCurrencyDropdown,
  })
  const dropdownOptions = isCurrencyDropdown
    ? getEnabledCurrencyDropDownOptions(
        factorConfigurationResponse?.factorConfiguration.enabledCurrencies,
        options,
        typeof props.value === 'string' ? props.value : undefined
      )
    : options

  const hasGroups = dropdownOptions.some((option) => Boolean(option.group))
  let lastGroup: string | undefined

  return (
    <TextField select={true} {...props}>
      {dropdownOptions.length > 0 ? (
        dropdownOptions.flatMap((option) => {
          // Grouped rendering is opt-in: only options that set `group` get a ListSubheader above
          // them (once per group, in list order) — ungrouped dropdowns are unaffected.
          const showHeader = hasGroups && option.group !== undefined && option.group !== lastGroup
          if (hasGroups) lastGroup = option.group
          // A grouped option's value can legitimately repeat across groups (e.g. a classic GL
          // code written to more than one ledger) — key on group+value so React siblings stay
          // unique, while `value` itself (used for selection) is untouched.
          const menuItem = (
            <MenuItem
              id={option.id}
              key={`${option.group ?? ''}-${option.value}`}
              value={option.value}
              disabled={option.disabled}
              sx={option.indentLevel ? { pl: 2 + option.indentLevel * 2 } : undefined}
            >
              {option.label}
            </MenuItem>
          )
          return showHeader
            ? [<ListSubheader key={`group-${option.group}`}>{option.group}</ListSubheader>, menuItem]
            : [menuItem]
        })
      ) : (
        <TypoGraph content={notOptionText ? notOptionText : 'No options found'} align="center" />
      )}
      {menuNode && menuNode}
    </TextField>
  )
}

export default DropdownField
